import '../src/app/i18n/i18n';
import React, { useEffect, useState } from 'react';
import {BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate} from 'react-router-dom';
import Login from '../src/app/modules/layout/Login/Login';
import './App.css';
import AuthCards from "../src/app/modules/layout/Login/AuthCards";
import { useDispatch, useSelector } from "react-redux";
import { getUserRequest } from "./app/redux/actions/userActions";
import JobManagement from "./app/modules/jobs/Jobmanagement";
import Sidebar from "./app/modules/layout/Sidebar/Sidebar";
import UpperBar from "./app/modules/layout/UpperBar/UpperBar";
import CompanyManagement from "./app/modules/compagnies/CompanyManagement";
import SidebarMenuManagement from "./app/modules/MenusManagment/SidebarMenuManagement";
import UserMassImport from "./app/modules/Users/UserMassImport";
import SeuilPassageManagement from "./app/modules/jobs/SeuilPassage/SeuilPassageManagement";
import Chatbot from "./app/modules/adminrh/Chatbot/Chatbot";

import { FormationRoutes } from "./routes/formationRoutes";
import { RecrutementRoutes } from "./routes/recrutementRoutes";
import { AdminGrhRoutes } from "./routes/adminGrhRoutes";
import GestionEmails from "./app/modules/layout/Emails/GestionEmails";
import EntiteManagement from "./app/modules/Users/entites/EntiteManagement";
import ModuleManagement from "./app/modules/layout/Modules/Modulemanagement";
import RoleManagement from "./app/modules/layout/Roles/Rolemanagement";
import ParamInfosUsers from "./app/modules/Profile/ParamInfosUsers";
import {SkillsRoutes} from "./routes/skillsRoutes";
import NotificationSettings from "./app/modules/adminrh/parametres/parametresJS/NotificationSettings";
import TaskSettings from "./app/modules/adminrh/parametres/parametresJS/TaskSettings";
import { markCurrentSessionEnded, sendSessionHeartbeat } from "./app/utils/sessionLifecycle";

const LogsPage = React.lazy(() => import("./app/modules/layout/Logs/LogsPage"));

const AuthenticatedApp = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const user = useSelector(state => state?.auth?.user);
    const currentRole = useSelector((state) => state.role?.selectedRole);

    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        const saved = localStorage.getItem("sidebarCollapsed");
        return saved === "true";
    });

    const [isResponsive, setIsResponsive] = useState(window.innerWidth <= 1200);

    const [isRTL, setIsRTL] = useState(document.documentElement.getAttribute('dir') === 'rtl');

    useEffect(() => {
        const handleResize = () => {
            setIsResponsive(window.innerWidth <= 1200);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const checkRTL = () => {
            const dir = document.documentElement.getAttribute('dir');
            const newIsRTL = dir === 'rtl';
            if (newIsRTL !== isRTL) {
                setIsRTL(newIsRTL);
            }
        };

        checkRTL();
        const interval = setInterval(checkRTL, 100);
        const observer = new MutationObserver(checkRTL);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['dir']
        });

        return () => {
            clearInterval(interval);
            observer.disconnect();
        };
    }, [isRTL]);

    useEffect(() => {
        const handleStorageChange = () => {
            const saved = localStorage.getItem("sidebarCollapsed");
            setIsSidebarCollapsed(saved === "true");
        };

        window.addEventListener('storage', handleStorageChange);

        const interval = setInterval(() => {
            const saved = localStorage.getItem("sidebarCollapsed");
            const newValue = saved === "true";
            if (newValue !== isSidebarCollapsed) {
                setIsSidebarCollapsed(newValue);
            }
        }, 100);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, [isSidebarCollapsed]);

    const pagesWithoutSidebar = ['/cards'];
    const isOnCardsPage = pagesWithoutSidebar.includes(location.pathname);

    const getHomeByRole = (roleObj) => {
        if (!roleObj) return '/';

        const roleName =
            typeof roleObj === 'string'
                ? roleObj
                : roleObj.name || roleObj.code || '';

        const r = roleName.toUpperCase();

        if (r.includes('MANAGER')) {
            return '/manager/dashboard';
        }

        if (r.includes('COLLAB') || r.includes('EMPLOYEE')) {
            return '/collaborateur/dashboard';
        }

        return '/dashboard';
    };

    useEffect(() => {
        if (!user?.id) return;
        if (!currentRole) return;

        // If on AuthCards page, do not redirect automatically
        if (location.pathname === '/cards') return;

        // If on any specific sub-page (e.g. /utilisateurs), do not redirect on mount/refresh
        if (location.pathname !== '/' && location.pathname !== '') return;

        const target = getHomeByRole(currentRole);
        if (location.pathname === target) return;

        navigate(target, { replace: true });
    }, [currentRole, user?.id, location.pathname]);

    const sidebarWidth = isResponsive ? 80 : (isSidebarCollapsed ? 80 : 260);

    return (
        <>
            {!isOnCardsPage && user?.id && (
                <>
                    <UpperBar />
                    <Sidebar user={user} />
                </>
            )}

            <div style={{
                marginLeft: isOnCardsPage ? 0 : (isRTL ? 0 : `${sidebarWidth}px`),
                marginRight: isOnCardsPage ? 0 : (isRTL ? `${sidebarWidth}px` : 0),
                width: isOnCardsPage ? '100%' : `calc(100% - ${sidebarWidth}px)`,
                minHeight: '100vh',
                transition: 'margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1), margin-right 0.4s cubic-bezier(0.4, 0, 0.2, 1), width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                paddingTop: isOnCardsPage ? 0 : '73px',
            }}>
                <Routes>
                    <Route path="/cards" element={<AuthCards />} />

                    <Route path="/postes" element={<JobManagement />} />
                    <Route path="/postes/seuils" element={<SeuilPassageManagement />} />
                    <Route path="/emails" element={<GestionEmails />} />
                    <Route path="/roles" element={<RoleManagement />} />
                    <Route path="/companies" element={<CompanyManagement />} />
                    <Route path="/utilisateurs" element={<UserMassImport />} />
                    <Route path="/permissions" element={<SidebarMenuManagement />} />
                    <Route path="/modules" element={<ModuleManagement />} />
                    <Route path="/gestion-emails/logs" element={
                        <React.Suspense fallback={<div>Chargement...</div>}>
                            <LogsPage />
                        </React.Suspense>
                    } />
                    <Route path="/company/info" element={<ParamInfosUsers />} />
                    <Route path="/entities" element={<EntiteManagement />} />
                    <Route path="/parametres/notifications" element={<NotificationSettings /> }/>
                    <Route path="/parametres/taches" element={<TaskSettings /> }/>

                    {FormationRoutes.map(route => (
                        <Route key={route.path} path={route.path} element={route.element} />
                    ))}
                    {RecrutementRoutes.map(route => (
                        <Route key={route.path} path={route.path} element={route.element} />
                    ))}
                    {AdminGrhRoutes.map(route => (
                        <Route key={route.path} path={route.path} element={route.element} />
                    ))}
                    {SkillsRoutes.map(route => (
                        <Route key={route.path} path={route.path} element={route.element} />
                    ))}
                </Routes>

                {/* Chatbot masqué sur la page /cards */}
                {/*{!isOnCardsPage && <Chatbot />}*/}
            </div>
        </>
    );
};

const App = () => {
    const dispatch = useDispatch();

    const isLoggedState = useSelector((state) => state.auth.isLogged);
    const currentUser = useSelector((state) => state.auth.user);

    const uid = sessionStorage.getItem("uid");
    const token = sessionStorage.getItem("token");

    const isLogged = Boolean(isLoggedState || (token && uid));

    useEffect(() => {
        if (uid && token && !currentUser) {
            dispatch(getUserRequest(uid));
        }
    }, [uid, token, currentUser, dispatch]);

    useEffect(() => {
        if (!isLogged) return undefined;

        const endSession = () => {
            markCurrentSessionEnded({ preferBeacon: true });
        };

        window.addEventListener('pagehide', endSession);
        window.addEventListener('beforeunload', endSession);

        return () => {
            window.removeEventListener('pagehide', endSession);
            window.removeEventListener('beforeunload', endSession);
        };
    }, [isLogged]);

    useEffect(() => {
        if (!isLogged) return undefined;

        sendSessionHeartbeat();
        const intervalId = window.setInterval(sendSessionHeartbeat, 60_000);

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                sendSessionHeartbeat();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isLogged]);

    return (
        <Router>
            <Routes>
                {!isLogged && (
                    <>
                        <Route path="/" element={<Login />} />
                        <Route path="*" element={<Navigate to="/" replace />} />

                    </>
                )}

                {isLogged && (
                    <>
                        <Route path="/*" element={<AuthenticatedApp />} />
                        <Route path="/" element={<Navigate to="/cards" replace />} />

                    </>
                )}
            </Routes>
        </Router>
    );
};

export default App;
